import {observable, computed, action, autorun, observe, reaction} from 'mobx';
import autobind from 'autobind-decorator';
import objectAssignDeep from 'utils/object-assign-deep';
import {nonenumerable} from 'spa/decorators';
import {async} from 'spa/server-wait';
import * as Sentry from '@sentry/react';

import MeState from './me';
import LoginState from './login';
import LoginV2State from './LoginV2';
import SignupState from './signup';
import SignupV2State from './signupV2';
import UpdateState from './update';
import FacebookState from './facebook';

export const AUTH_COOKIE_NAME = 'auth';

class SessionState {
  @observable token;
  @observable _me;
  @observable _login;
  @observable _signup;
  @observable _update;
  @observable _facebook;

  @computed get maybeLoggedIn() {
    return !!this.token;
  }

  @computed get isLoading() {
    return (this._me && this._me.isLoading) ||
      (this._login && this._login.isLoading) ||
      (this._signup && this._signup.isLoading) ||
      (this._facebook && this._facebook.isLoading) ||
      false;
  }
  @computed get isLoadingFacebook() {
    return (this._facebook && this._facebook.isLoading) ||
      false;
  }
  @computed get error() {
    return (this._me && this._me.error) ||
      (this._login && this._login.error) ||
      (this._signup && this._signup.error) ||
      (this._facebook && this._facebook.error) ||
      null;
  }

  @computed get isVerificationChallengeRequired() {
    const signupVerificationRequired = this._signup &&
      this._signup.error &&
      this._signup.error.json &&
      this._signup.error.json.code === 'ACCOUNT_VERIFY_CHALLENGE';
    const loginVerificationRequired = this._login &&
      this._login.error &&
      this._login.error.json &&
      this._login.error.json.code === 'ACCOUNT_VERIFY_CHALLENGE';

    return signupVerificationRequired || loginVerificationRequired || false;
  }

  @computed get loginResponse() {
    return (this._login && this._login.response) ||
      (this._facebook && this._facebook.response) ||
      null;
  }
  @computed get loginError() {
    return this._login && this._login.error ||
      null;
  }
  @computed get signupResponse() {
    return this._signup && this._signup.response ||
      null;
  }
  @computed get signupError() {
    return this._signup && this._signup.error ||
      null;
  }
  @computed get updateIsLoading() {
    return this._update && this._update.isLoading ||
      false;
  }
  @computed get updateResponse() {
    return this._update && this._update.response ||
      null;
  }
  @computed get updateError() {
    return this._update && this._update.error ||
      null;
  }

  @computed get isLoggedIn() {
    return !!this.user;
  }

  @observable user;

  @autobind @action load() {
    if (this.maybeLoggedIn) {
      if (this._login) this._login.reset();
      if (this._facebook) this._facebook.reset();
      if (this._signup) this._signup.reset();
      if (!this._me) {
        this._me = new MeState(this.ctx);
      }
      this._me.maybeLoad()
        .then(action('load success', () => {
          this.user = this._me.response;
        }));
    }
  }

  @autobind @action refresh(onSuccessRefresh) {
    if (this._me) this._me.reset();

    // WE NEED THIS TIMEOUT UNTIL WE FIX THE RACE CONDITION ISSUE IN THE BACKEND
    setTimeout(() => {
      if (this.maybeLoggedIn) {
        if (this._login) this._login.reset();
        if (this._facebook) this._facebook.reset();
        if (this._signup) this._signup.reset();
        if (!this._me) {
          this._me = new MeState(this.ctx);
        }

        this._me.maybeLoad()
          .then(action('load success', () => {
            this.user = this._me.response;
            onSuccessRefresh();
          }));
      }
    }, 1000);
  }

  @autobind @action getToken({
    email, password,
  }) {
    if (!this._login) {
      this._login = new LoginState(this.ctx);
    }
    return this._login.load({}, {
      email, password,
    })
      .then(this.onGetTokenSuccess);
  }

  @autobind @action onGetTokenSuccess(response) {
    return response;
  }

  @autobind @action login({
    discountId, email, password, oneTimeLogin, signupCampaign_id,
  }) {
    this.token = null;
    if (this._me) this._me.reset();
    if (this._facebook) this._facebook.reset();
    if (this._signup) this._signup.reset();
    if (!this._login) {
      this._login = new LoginState(this.ctx);
    }
    return this._login.load({}, {
      email, password, oneTimeLogin, signupCampaign_id,
    })
      .then(this.onLoginSuccess);
  }

  @autobind
  @action('loginV2 - Session')
  loginV2({
    email, password,
  }) {
    this.token = null;
    if (this._me) this._me.reset();
    if (this._facebook) this._facebook.reset();
    if (this._signup) this._signup.reset();

    if (!this._login) {
      this._login = new LoginV2State(this.ctx);
    }
    return this._login.load({}, {
      email, password,
    })
      .then(this.onLoginSuccess);
  }

  @autobind @action onLoginSuccess(response) {
    if (response && response.user && response.user.meta && response.user.meta.block) {
      this.logout();
      window.location.href = `/${this.ctx.i18n.lc}/account-blocked`;

      return;
    }

    this.token = response.token;
    this.user = response.user;

    // We need to call /me in order to get full user object, since login response doesn't return
    // the full user
    this.refresh();

    return response;
  }

  @autobind @action signup({
    name, homeCity_id, email, password, telephone, ...rest
  }) {
    this.token = null;
    if (this._me) this._me.reset();
    if (this._login) this._login.reset();
    if (this._facebook) this._facebook.reset();
    if (!this._signup) {
      this._signup = new SignupState(this.ctx);
    }
    return this._signup.load({}, {
      name, homeCity_id, email, password, telephone, ...rest,
    })
      .then(this.onSignupSuccess);
  }

  @autobind @action signupV2({
    name, homeCity_id, email, password, telephone, ...rest
  }) {
    this.token = null;
    if (this._me) this._me.reset();
    if (this._login) this._login.reset();
    if (this._facebook) this._facebook.reset();
    if (!this._signup) {
      this._signup = new SignupV2State(this.ctx);
    }
    return this._signup.load({}, {
      name, homeCity_id, email, password, telephone, ...rest,
    })
      .then(this.onSignupSuccess);
  }
  @autobind @action onSignupSuccess(response) {
    this.token = response.token;
    this.user = response.user;

    // We need to call /me in order to get full user object, since signup response doesn't return
    // the full user
    this.refresh();

    return response;
  }

  @autobind @async @action async logout() {
    this.ctx.basket.reset();
    this.ctx.billing.reset();

    this.token = null;
    this.user = null;
    if (this._me) this._me.reset();
    if (this._login) this._login.reset();
    if (this._facebook) this._facebook.reset();
    if (this._signup) this._signup.reset();
    if (this._update) this._update.reset();

    this.ctx.setCookie('jsunum', null, { expires: new Date('1993-08-25'), path: '/' });

    // Unset our user scope on logout
    Sentry.configureScope(scope => scope.setUser(null));
  }

  @autobind @action async update(body) {
    if (!this.user) return null;
    if (this._me) this._me.reset();
    if (this._login) this._login.reset();
    if (this._facebook) this._facebook.reset();
    if (this._signup) this._signup.reset();
    if (!this._update) {
      this._update = new UpdateState(this.ctx);
    }
    return this._update.load({}, body)
      .then(action('update success', () => {
        this.user = this._update.response.user;
      }));
  }
  @autobind @action resetUpdate() {
    if (this._update) this._update.reset();
  }
  @autobind @action resetSignup() {
    if (this._signup) this._signup.reset();
  }
  @autobind @action resetLogin() {
    if (this._login) this._login.reset();
  }

  /*
   * Resets any session errors
   */
  @autobind @action resetError() {
    if (this._me) this._me.error = undefined;

    if (this._login) this.login.error = undefined;

    if (this._signup) this._signup.error = undefined;

    if (this._facebook) this._facebook.error = undefined;
  }

  @autobind @action initFacebook() {
    if (!this._facebook) {
      this._facebook = new FacebookState(this.ctx);
    }
    return this._facebook.init();
  }
  @autobind @action facebookLogin(...args) {
    if (this._me) this._me.reset();
    if (this._login) this._login.reset();
    if (this._signup) this._signup.reset();
    if (this._update) this._update.reset();
    if (!this._facebook) {
      this._facebook = new FacebookState(this.ctx);
    }
    return this._facebook.load(...args)
      .then(this.onFacebookLoginSuccess);
  }
  @autobind @action onFacebookLoginSuccess(response) {
    this.token = response.token;
    this.user = response.user;

    // We need to call /me in order to get full user object, since login response doesn't return
    // the full user
    this.refresh();

    return response;
  }

  async setMeta(key, value) {
    if (this.user) {
      // set in API
      const update = {[key]: value};
      await this.ctx.sdk.put('/me/meta', {}, update);
      Object.assign(this.user.meta, update);
    } else {
      // set in cookie
      const currentValue = this.ctx.getCookie('userMeta') || '{}';
      const obj = JSON.parse(currentValue);
      obj[key] = value;

      const stringified = JSON.stringify(obj);
      this.ctx.setCookie('userMeta', stringified, {
        expires: 1460,
      });
    }
  }

  async getMeta(key) {
    if (this.user) {
      const response = await this.ctx.sdk.get('/me/meta', {});
      this.user.meta = response.meta;
      return response.meta[key];
    }
    // get from cookie
    const currentValue = this.ctx.getCookie('userMeta') || '{}';
    const obj = JSON.parse(currentValue);
    return obj[key];
  }

  async getCachedMeta(key) {
    if (this.user) {
      return this.user.meta[key];
    }
    return this.getMeta(key);
  }

  async getMetaOnce(key, value) {
    if (this.getCachedMeta(key) == null) {
      return this.set(key, value);
    }
    return undefined;
  }

  @autobind @action async convertMeta() {
    // this method can be called on each page load
    // it will read the current cookie value and move any props to the proper user meta data
    if (this.user == null) {
      // not logged in
      return;
    }

    const currentValue = this.ctx.getCookie('userMeta');
    if (!currentValue) {
      // no data to copy
      return;
    }

    try {
      const obj = JSON.parse(currentValue);

      await this.ctx.sdk.put('/me/meta', {}, obj);

      // saved ok, wipe the cookie
      this.ctx.setCookie('userMeta', '', {expires: new Date('1993-08-25')});
    } catch (error) {
      console.log('window.umjs.profile.meta#convert error', error);
    }
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.listeners.push(
      observe(this, 'token', action('set session cookie', () => {
        const maxAge = 1200000000;
        ctx.setCookie(AUTH_COOKIE_NAME, this.token || '', { maxAge, path: '/' });
      })),
      reaction(() => this.user, this.convertMeta, {fireImmediately: true, name: 'convert meta'}),
      autorun(() => {
        if (this.error && this.error.json && this.error.json.code === 410) {
          this.logout();
        }
      }, {
        name: 'logout when session expires',
      }),
      reaction(
        () => this.user,
        (user) => {
          ctx.tracker.identify(user);

          if (user && user.id) {
            Sentry.configureScope(scope => scope.setUser({id: user.id }));

            const maxAge = 1200000000;
            ctx.setCookie('jsunum', user.id, { maxAge, path: '/' });
          }

          // Get the embedded messages campaigns from iterable for this user
          ctx.iterable.getCampaigns();
        },
        {
          name: 'tracker#identify',
          fireImmediately: true,
        },
      ),
    );
  }

  @action deserialize(value) {
    const {
      _me, _login, _signup, _update, _facebook, ...rest
    } = value;
    objectAssignDeep(this, rest);
    if (_me) {
      this._me = new MeState(this.ctx);
      objectAssignDeep(this._me, _me);
    }
    if (_login) {
      this._login = new LoginState(this.ctx);
      objectAssignDeep(this._login, _login);
    }
    if (_signup) {
      this._signup = new SignupState(this.ctx);
      objectAssignDeep(this._signup, _signup);
    }
    if (_update) {
      this._update = new UpdateState(this.ctx);
      objectAssignDeep(this._update, _update);
    }
    if (_facebook) {
      this._facebook = new FacebookState(this.ctx);
      objectAssignDeep(this._facebook, _facebook);
    }
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default SessionState;



// WEBPACK FOOTER //
// ./spa/state/session/index.jsx